import {
  EventEmitter,
  Injectable,
  NgZone,
  Signal,
  signal,
} from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ApiService } from './api.service';
import { Hydra } from 'src/app/interfaces/hydra';
import { firstValueFrom, Subscription } from 'rxjs';
import { Participation } from '../interfaces/participation';
import { Project } from 'src/app/interfaces/project';
import { ParticipationCounts } from '../interfaces/participation-counts';
import { ProjectService } from './project.service';
import { RatingService } from './rating.service';
import { SurveyService } from './survey.service';
import { PollService } from './poll.service';
import { TicketService } from './ticket.service';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class ParticipationService {
  private PARTICIPATION_COUNT_REFRESH_INTERVAL = 180000; // 3min
  private currentRequest: Subscription = null;
  private readonly _openCount = signal<number>(0);
  public readonly openCount: Signal<number> = this._openCount.asReadonly();
  public lastViewDataUpdated$ = new EventEmitter();
  private readonly HAS_PARTICIPATED_KEY = 'hasParticipated';
  private _hasParticipated = signal<boolean>(false);
  public readonly hasParticipated = this._hasParticipated.asReadonly();

  constructor(
    private apiService: ApiService,
    private projectService: ProjectService,
    private ratingService: RatingService,
    private surveyService: SurveyService,
    private pollService: PollService,
    private ticketService: TicketService,
    private storageService: StorageService,
    private ngZone: NgZone
  ) {}

  init() {
    this.storageService
      .get(this.HAS_PARTICIPATED_KEY)
      .then((value) => this._hasParticipated.set(value === true));

    this.updateOpenCount();

    this.projectService.starChanged$.subscribe(() => {
      setTimeout(() => this.updateOpenCount(), 500);
    });

    this.ratingService.ratingSubmitted.subscribe((item) => {
      if (item === null) {
        return;
      }

      this._openCount.update((n) => n - 1);
      this.setHasParticipated(true);
    });

    this.surveyService.surveySubmitted.subscribe(() => {
      this._openCount.update((n) => n - 1);
      this.setHasParticipated(true);
    });

    this.pollService.pollVoted.subscribe(() => {
      this._openCount.update((n) => n - 1);
      this.setHasParticipated(true);
    });

    this.ticketService.lastViewDataUpdated$.subscribe(() =>
      this.updateOpenCount()
    );

    this.ngZone.runOutsideAngular(() =>
      setInterval(
        () => this.ngZone.run(() => this.updateOpenCount()),
        this.PARTICIPATION_COUNT_REFRESH_INTERVAL
      )
    );
  }

  public getOpenItems(
    urlParams?: HttpParams,
    cancelPending: boolean = false
  ): Promise<Hydra<Participation>> {
    return new Promise((resolve, reject) => {
      const params = urlParams ?? new HttpParams();

      if (cancelPending && this.currentRequest != null) {
        this.currentRequest.unsubscribe();
      }

      const observable = this.apiService.identifiedGet(
        '/api/v5/participation/open/list',
        params
      );
      this.currentRequest = observable.subscribe(
        (response) => {
          const responseData = response;
          const obj = <Hydra<Participation>>{};

          obj.member = <Participation[]>responseData['hydra:member'];

          obj.totalItems = responseData['hydra:totalItems'];

          resolve(obj);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  public async updateOpenCount(): Promise<void> {
    try {
      const n = await firstValueFrom(
        this.apiService.identifiedGet('/api/v5/participation/open/count', null)
      );
      this._openCount.set(n.count);
    } catch (error) {
      this._openCount.set(0);
    }
  }

  public async getOpenCounts(): Promise<ParticipationCounts> {
    return firstValueFrom(
      this.apiService.identifiedGet('/api/v5/participation/open/counts', null)
    );
  }

  public async updateLastViewData(project: Project) {
    await firstValueFrom(
      this.apiService.identifiedPut(
        `/api/v3/projects/${project.slug}/participation/view-data`,
        null
      )
    );
    this.lastViewDataUpdated$.emit();
  }

  private setHasParticipated(value: boolean) {
    this._hasParticipated.set(value);
    this.storageService.set(this.HAS_PARTICIPATED_KEY, value);
  }
}
